import { useState, useEffect } from 'react';
import { indicadoresService, IndicadorEmpresa, HistorialIndicador, IndicadorBase, Variable } from '@/services/indicadores.service';
import { adminIndicadoresService } from '@/services/sysadmin.service';
import type { IndicadorBaseSysadmin } from '@/services/sysadmin.service';

export function useIndicadoresByEmpresa(nit: string) {
  const [data, setData] = useState<IndicadorEmpresa[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchIndicadores = async () => {
    if (!nit) return;
    setIsLoading(true);
    setError(null);
    try {
      const response = await indicadoresService.listarIndicadoresEmpresa(nit);
      setData(response.indicadores || []);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error al cargar indicadores');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchIndicadores();
  }, [nit]);

  return { data, isLoading, error, refetch: fetchIndicadores };
}

export function useIndicadorById(nit: string, indicadorId: number) {
  const [data, setData] = useState<IndicadorEmpresa | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!nit || !indicadorId) return;

    const fetchIndicador = async () => {
      setIsLoading(true);
      setError(null);
      try {
        const response = await indicadoresService.obtenerIndicadorEmpresaId(nit, indicadorId);
        setData(response.indicador);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Error al cargar indicador');
      } finally {
        setIsLoading(false);
      }
    };

    fetchIndicador();
  }, [nit, indicadorId]);

  return { data, isLoading, error };
}

export function useHistorialIndicadores(nit: string) {
  const [data, setData] = useState<HistorialIndicador[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!nit) return;

    const fetchHistorial = async () => {
      setIsLoading(true);
      setError(null);
      try {
        const response = await indicadoresService.obtenerHistorialIndicadores(nit);
        setData(response.historial || []);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Error al cargar historial');
      } finally {
        setIsLoading(false);
      }
    };

    fetchHistorial();
  }, [nit]);

  return { data, isLoading, error };
}

export function useIndicadoresBase(nit: string) {
  const [data, setData] = useState<IndicadorBase[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!nit) return;

    const fetchIndicadoresBase = async () => {
      setIsLoading(true);
      setError(null);
      try {
        const response = await indicadoresService.obtenerIndicadoresBase(nit);
        setData(response.baseIndicadores || []);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Error al cargar indicadores base');
      } finally {
        setIsLoading(false);
      }
    };

    fetchIndicadoresBase();
  }, [nit]);

  return { data, isLoading, error };
}

export function useVariablesIndicador(nit: string, indicadorId: number) {
  const [data, setData] = useState<Variable[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!nit || !indicadorId) return;

    const fetchVariables = async () => {
      setIsLoading(true);
      setError(null);
      try {
        const response = await indicadoresService.listarVariablesIndicadorEmpresa(nit, indicadorId);
        setData(response.variables || []);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Error al cargar variables');
      } finally {
        setIsLoading(false);
      }
    };

    fetchVariables();
  }, [nit, indicadorId]);

  return { data, isLoading, error };
}

export function useIndicadoresBaseSysadmin() {
  const [data, setData] = useState<IndicadorBaseSysadmin[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchIndicadoresBase = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const response = await adminIndicadoresService.listar();
      setData(response.baseIndicadores || []);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error al cargar indicadores base');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchIndicadoresBase();
  }, []);

  return { data, isLoading, error, refetch: fetchIndicadoresBase };
}

export function useAsignarIndicador(nit: string) {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const asignar = async (indicadorBaseId: number): Promise<{ success: boolean; error?: string }> => {
    setIsLoading(true);
    setError(null);
    try {
      await indicadoresService.asignarIndicadorAEmpresa(nit, indicadorBaseId);
      return { success: true };
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Error al asignar indicador';
      setError(msg);
      return { success: false, error: msg };
    } finally {
      setIsLoading(false);
    }
  };

  return { asignar, isLoading, error };
}

export function useEliminarIndicadorEmpresa(nit: string) {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const eliminar = async (indicadorId: number): Promise<{ success: boolean; error?: string }> => {
    setIsLoading(true);
    setError(null);
    try {
      await indicadoresService.eliminarIndicadorDeEmpresa(nit, indicadorId);
      return { success: true };
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Error al eliminar indicador';
      setError(msg);
      return { success: false, error: msg };
    } finally {
      setIsLoading(false);
    }
  };

  return { eliminar, isLoading, error };
}

export function useCrearIndicadorBase() {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const crear = async (data: {
    nombre: string;
    formula: string;
    frecuencia: 'MENSUAL' | 'TRIMESTRAL' | 'SEMESTRAL' | 'ANUAL';
    fuenteDatos: string;
    meta: number;
    tipoResultado: 'PORCENTAJE' | 'NUMERO' | 'MONEDA';
    variables: { nombre: string; orden?: number }[];
  }): Promise<{ success: boolean; error?: string }> => {
    setIsLoading(true);
    setError(null);
    try {
      await adminIndicadoresService.crear(data);
      return { success: true };
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Error al crear indicador';
      setError(msg);
      return { success: false, error: msg };
    } finally {
      setIsLoading(false);
    }
  };

  return { crear, isLoading, error };
}

export function useEliminarIndicadorBase() {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const eliminar = async (id: number): Promise<{ success: boolean; error?: string }> => {
    setIsLoading(true);
    setError(null);
    try {
      await adminIndicadoresService.eliminar(id);
      return { success: true };
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Error al eliminar indicador base';
      setError(msg);
      return { success: false, error: msg };
    } finally {
      setIsLoading(false);
    }
  };

  return { eliminar, isLoading, error };
}
